import React, { useEffect, useRef } from 'react';
interface HyperspeedProps {
  className?: string;
  /** Number of streaks */
  count?: number;
  /** Streak speed multiplier */
  speed?: number;
  /** Streak color (rgb tuple as string) */
  color?: string;
}
/**
 * Star-streak warp effect — points fly outward from the center, leaving
 * a tail that lengthens with distance. Pure canvas. Great for moments
 * that need a sense of acceleration or "system online".
 */
export function Hyperspeed({
  className = '',
  count = 220,
  speed = 1,
  color = '26,143,212'
}: HyperspeedProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const w = parent.offsetWidth;
      const h = parent.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);
    type Star = {
      x: number;
      y: number;
      z: number;
      pz: number;
    };
    const stars: Star[] = [];
    const W = () => canvas.width / dpr;
    const H = () => canvas.height / dpr;
    const respawn = (s: Star) => {
      s.x = (Math.random() - 0.5) * W();
      s.y = (Math.random() - 0.5) * H();
      s.z = Math.random() * W();
      s.pz = s.z;
    };
    for (let i = 0; i < count; i++) {
      const s = {
        x: 0,
        y: 0,
        z: 0,
        pz: 0
      };
      respawn(s);
      stars.push(s);
    }
    const tick = () => {
      const w = W();
      const h = H();
      // Trail effect by fading previous frame slightly
      ctx.fillStyle = 'rgba(5, 6, 8, 0.35)';
      ctx.fillRect(0, 0, w, h);
      ctx.translate(w / 2, h / 2);
      for (const s of stars) {
        s.pz = s.z;
        s.z -= 6 * speed;
        if (s.z < 1) {
          respawn(s);
          continue;
        }
        const sx = s.x / s.z * w;
        const sy = s.y / s.z * h;
        const px = s.x / s.pz * w;
        const py = s.y / s.pz * h;
        const dist = Math.sqrt(sx * sx + sy * sy);
        const alpha = Math.min(1, dist / (w * 0.4));
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${color}, ${alpha * 0.9})`;
        ctx.lineWidth = Math.max(0.5, alpha * 1.5);
        ctx.moveTo(px, py);
        ctx.lineTo(sx, sy);
        ctx.stroke();
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, [count, speed, color]);
  return <canvas ref={canvasRef} className={`absolute inset-0 pointer-events-none ${className}`} data-id="element-222" />;
}